import {useEffect} from 'react'
import {getRoomId} from '@xc/hiya-jsbridge'
import {HiyaVerHost} from 'hiya-common/src/constant'
import {GetRechargeSchemeParams} from 'hiya-common/src/utils/goRechargePage'
import {is_hit_first_recharge} from '~/pages/NewWallet/service'

/** 货币模式：0 金币场，1 银币场 */
export enum CurrencyMode {
  Gold = 0,
  Silver = 1,
}

/**
 * 引导弹窗 USD 金额：最多 1 位小数，小于 0.1 显示 0.1
 */
export function formatMissAmountUsd(amount?: number | null): string {
  const raw = Number(amount)
  const safe = Number.isFinite(raw) ? raw : 0
  const clamped = safe < 0.1 ? 0.1 : safe
  const rounded = Math.round(clamped * 10) / 10
  return rounded.toLocaleString('en-US', {maximumFractionDigits: 1})
}

export const isSilverCurrencyMode = (mode?: number) =>
  mode === CurrencyMode.Silver

/** 银币游戏可见且当前为银币场 */
export const isChipGameSilverActive = (
  chipGameVisible?: boolean,
  currencyMode?: number
) => !!(chipGameVisible && isSilverCurrencyMode(currencyMode))

/** 筹码游戏跳转钱包时，银币场对应筹码 tab 下标 */
export const CHIP_GAME_WALLET_TAB = 1

/** 银币场跳转钱包筹码 tab，金币场走 opts 默认充值 tab */
export function goChipGameRecharge(
  goRecharge: (opts?: GetRechargeSchemeParams) => void,
  isSilverActive: boolean,
  opts?: GetRechargeSchemeParams
) {
  if (isSilverActive) {
    goRecharge({...opts, tab: CHIP_GAME_WALLET_TAB})
    return
  }
  goRecharge(opts)
}

/** 银币模式下展示银币余额，否则展示金币余额 */
export const getDisplayBalance = (
  isSilverActive: boolean,
  goldBalance?: number,
  silverBalance?: number
) => (isSilverActive ? silverBalance ?? 0 : goldBalance ?? 0)

/** 达到银币限制时强制切回金币场 */
export const resolveCurrencyModeOnLimit = (
  currencyMode?: number,
  limitReached?: boolean
) => {
  if (limitReached && isSilverCurrencyMode(currencyMode)) {
    return CurrencyMode.Gold
  }
  return currencyMode
}

/** 达到银币限制时自动切回金币场 */
export function useChipGameLimitReachedEffect(
  chipGameLimitReached: boolean | undefined,
  currencyMode: number | undefined,
  onSwitchToGold: () => void
) {
  useEffect(() => {
    if (chipGameLimitReached && isSilverCurrencyMode(currencyMode)) {
      onSwitchToGold()
    }
  }, [chipGameLimitReached, currencyMode, onSwitchToGold])
}

/** 首冲挂件倒计时：3 天 */
export const FIRST_RECHARGE_COUNTDOWN_MS = 3 * 24 * 60 * 60 * 1000

export const ROULETTE_FIRST_RECHARGE_KEY =
  'silver_guide_first_recharge_shown_roulette'
export const LUCKY_SLOT_FIRST_RECHARGE_KEY =
  'silver_guide_first_recharge_shown_lucky_slot'
export const GREEDY_SPORTS_FIRST_RECHARGE_KEY =
  'silver_guide_first_recharge_shown_greedy_sports'

/** 读取首冲挂件剩余倒计时，无标识或已过期返回 null */
export function getFirstRechargeCountdownLeft(
  storageKey: string
): number | null {
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const startTime = Number(raw)
    if (!Number.isFinite(startTime) || startTime <= 0) return null
    const left = startTime + FIRST_RECHARGE_COUNTDOWN_MS - Date.now()
    return left > 0 ? left : null
  } catch {
    return null
  }
}

/**
 * 打开首冲弹窗（命中首冲人群时）
 */
export async function open_first_recharge_modal() {
  const {is_hit} = await is_hit_first_recharge({from: 'game_coin_guide'})
  if (!is_hit) return false
  const room_id = await getRoomId()
  const fullscreen = room_id ? 2 : 1
  const otherInfo = room_id
    ? {from: 'chipsgame', bg_clear: true}
    : {from: 'chipsgame'}
  const halfScreenParams = room_id ? '&bg_clear=1&disable_native_gesture=1' : ''
  window.HiyaJSBridge.callHandler('openScheme', {
    url: `hiya://openWindow?fullscreen=${fullscreen}${halfScreenParams}&external=0&need_user_info=1&other_info=${encodeURIComponent(
      JSON.stringify(otherInfo)
    )}&url=${HiyaVerHost}/FirstCharge`,
  })
  return true
}

/**
 * 引导切换金币模式后仅首次触发首冲弹窗
 */
export async function trigger_first_recharge_modal_once(storageKey: string) {
  try {
    if (window.localStorage.getItem(storageKey)) return false
    window.localStorage.setItem(storageKey, String(Date.now()))
  } catch (e) {
    // localStorage 不可用时，不阻断首冲弹窗逻辑
  }
  return open_first_recharge_modal()
}
